import { ITranscription, ITranscriptionManager } from '../interfaces/ITranscriptionManager'
import 'isomorphic-fetch';
import { IStoredTranscription, TranscriptionStorageType } from '../interfaces/IStoredTranscription'
import { TranscriptionData } from '../utils/TranscriptionData';
import { SpeakerNameManager } from '../manager/speakerNameManager/SpeakerNameManager';
import { TRANSCRIPT_STORAGE_VERSION } from '../factory/TranscriptionManagerFactory';
import { HostAppSettingName, HostAppSettings } from '../office-js/HostAppSettings';
import { IStoredTranscriptionArray } from '../interfaces/IStoredTranscriptionArray';
import { DocumentStorage } from '../office-js/DocumentStorage';
import { LogEvents } from '../../utils/LogEvents';
import { DurationMeasureType } from '../interfaces/ILogEvent';
import { DocumentStorageKeys } from '../office-js/telemetry/DocumentStorageKeys';
import { ErrorStringFromException, isNullOrUndefined } from '../../utils/utils';

export class MultiTranscriptionDocumentStorageManager implements ITranscriptionManager {
    private transcriptionDataArr: TranscriptionData[];
    private thresholdLimit: number = Number.POSITIVE_INFINITY;
    private documentStorage: DocumentStorage;
    private storageVersion: number;

    public constructor(documentStorage: DocumentStorage, storedTransriptionArr: IStoredTranscription[], version: number = TRANSCRIPT_STORAGE_VERSION, thresholdLimit?: number) {
        if (isNullOrUndefined(documentStorage)) {
            throw new Error("Document Storage can't be null");
        }
        if (thresholdLimit !== undefined && thresholdLimit > 0) {
            this.thresholdLimit = thresholdLimit;
        }
        this.storageVersion = version;
        const storedTranscriptionSize = this.thresholdLimit < storedTransriptionArr.length ? this.thresholdLimit : storedTransriptionArr.length;
        this.transcriptionDataArr = new Array<TranscriptionData>();
        for (let i = 0; i < storedTranscriptionSize; i++) {
            this.transcriptionDataArr[i] = new TranscriptionData(storedTransriptionArr[i]);
        }
        this.documentStorage = documentStorage;
    }

    public GetTranscription(storageIndex: number = 0): ITranscription {
        if (storageIndex >= this.transcriptionDataArr.length || storageIndex < 0) {
            return {
                transcriptSegments: [],
            };
        }
        return this.transcriptionDataArr[storageIndex].GetTranscription();
    }

    public async UpdateTranscriptionSegment(segmentIndex: number, segmentText: string, speakerName: string | null, updateAll: boolean, storageIndex: number = 0) {
        if (storageIndex < 0 || storageIndex >= this.transcriptionDataArr.length)
            throw new Error('Invalid storage index');

        let transcription: ITranscription = this.transcriptionDataArr[storageIndex].GetTranscription();

        if (isNullOrUndefined(transcription.transcriptSegments))
            throw new Error("this.transcription.transcriptSegments is null");

        if (segmentIndex < 0 || segmentIndex >= transcription.transcriptSegments.length)
            throw new Error('Invalid segment index');

        transcription.transcriptSegments[segmentIndex].text = segmentText;

        let speakerNameManager: SpeakerNameManager = this.transcriptionDataArr[storageIndex].GetSpeakerNameManager();
        if (speakerNameManager != null && speakerName != null)
            speakerNameManager.UpdateSpeakerName(segmentIndex, speakerName, updateAll);

        await this.WriteStoredTranscription(this.GetAllStoredTranscription());
    }

    public GetStoredTranscription(storageIndex: number = 0): IStoredTranscription {
        if (storageIndex >= this.transcriptionDataArr.length || storageIndex < 0) {
            return {
                storageType: TranscriptionStorageType.DocumentStorage,
                descriptor: {}
            }
        }
        return this.transcriptionDataArr[storageIndex].GetStoredTranscription();
    }

    public async WriteTranscriptionToStorage(storedTranscription: IStoredTranscription) {
        const transciptionData: TranscriptionData = new TranscriptionData(storedTranscription);
        this.transcriptionDataArr.unshift(transciptionData);
        if (this.transcriptionDataArr.length > this.thresholdLimit) {
            this.transcriptionDataArr.pop();
        }
        const storedTranscriptionArray = this.GetAllStoredTranscription();
        await this.WriteStoredTranscription(storedTranscriptionArray);
    }

    public async DeleteTranscription(storageIndex: number = -1) {
        if (storageIndex == -1) {
            this.transcriptionDataArr.pop();
        } else {
            this.transcriptionDataArr.splice(storageIndex, 1);
        }
        if (this.transcriptionDataArr.length == 0) {
            await this.DeleteStoredTranscription();
        } else {
            const storedTranscriptionArray = this.GetAllStoredTranscription();
            await this.WriteStoredTranscription(storedTranscriptionArray);
        }
    }

    public IsMultiTranscriptStorageSupported(): boolean {
        return true;
    }

    public GetStorageVersion(): number {
        return this.storageVersion;
    }

    private GetAllStoredTranscription(): IStoredTranscriptionArray {
        let storedTranscriptionArr = new Array<IStoredTranscription>();
        for (let i = 0; i < this.transcriptionDataArr.length; i++) {
            storedTranscriptionArr[i] = this.transcriptionDataArr[i].GetStoredTranscription();
        }
        return {
            version: TRANSCRIPT_STORAGE_VERSION,
            storageType: TranscriptionStorageType.DocumentStorage,
            storedTranscriptions: storedTranscriptionArr
        };
    }

    private async WriteStoredTranscription(storedTranscriptionArray: IStoredTranscriptionArray): Promise<void> {
        LogEvents.DocumentStorage.Start();
        LogEvents.DocumentStorage.BeginDurationMeasure(DurationMeasureType.Normal, DocumentStorageKeys.WriteStoredTranscriptionDuration);

        try {
            await this.documentStorage.WriteTranscribeContentToClient(JSON.stringify(storedTranscriptionArray));
            HostAppSettings.Set(HostAppSettingName.HasStoredTranscription, true);
            HostAppSettings.Set(HostAppSettingName.StoredTranscription, storedTranscriptionArray);
            LogEvents.DocumentStorage.End();
        } catch (e) {
            LogEvents.DocumentStorage.End([{ key: DocumentStorageKeys.FailureReason, value: ErrorStringFromException(e) }]);
            throw e;
        }
    }

    async DeleteStoredTranscription(): Promise<void> {
        LogEvents.DocumentStorage.Start();
        LogEvents.DocumentStorage.BeginDurationMeasure(DurationMeasureType.Normal, DocumentStorageKeys.DeleteStoredTranscriptionDuration);

        try {
            await this.documentStorage.DeleteTranscribeContentFromClient();
            HostAppSettings.Set(HostAppSettingName.HasStoredTranscription, false);
            HostAppSettings.Set(HostAppSettingName.StoredTranscription, null);
            LogEvents.DocumentStorage.End();
        } catch (e) {
            LogEvents.DocumentStorage.End([{ key: DocumentStorageKeys.FailureReason, value: ErrorStringFromException(e) }]);
            throw e;
        }
    }
}